import { useState, FormEvent } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileCTA from '../../components/feature/MobileCTA';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    serviceNeeded: '',
    projectDetails: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('serviceNeeded', formData.serviceNeeded);
      formDataToSend.append('projectDetails', formData.projectDetails);
      const response = await fetch('https://readdy.ai/api/form/d6s0gctsbgrc47fimpr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', city: '', serviceNeeded: '', projectDetails: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileCTA />

      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-[#1A2B4A]">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <p className="text-[12px] text-[#C8A96E] uppercase tracking-widest font-bold mb-3">Get In Touch</p>
          <h1 className="text-[44px] lg:text-[52px] font-extrabold text-white mb-4">
            Let's Talk About Your Project
          </h1>
          <p className="text-[17px] text-white/70 max-w-[600px] mx-auto leading-relaxed">
            Call, text, or fill out the form below. We'll get back to you quickly with a free estimate.
          </p>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-[#C8A96E] py-4">
        <div className="max-w-[1000px] mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="tel:+12695551234" className="flex items-center gap-2 text-white font-bold text-[15px] hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i> (269) 555-1234
            </a>
            <span className="hidden sm:block text-white/50">|</span>
            <a href="sms:+12695551234" className="flex items-center gap-2 text-white font-bold text-[15px] hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-message-3-fill text-lg"></i> Send a Text
            </a>
            <span className="hidden sm:block text-white/50">|</span>
            <span className="flex items-center gap-2 text-white font-bold text-[15px] whitespace-nowrap">
              <i className="ri-translate-2 text-lg"></i> Hablamos Español
            </span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-[#F8F7F5]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">

            {/* Contact Info */}
            <div>
              <p className="text-[12px] text-[#C8A96E] uppercase tracking-widest font-bold mb-2">Contact Info</p>
              <h2 className="text-[28px] lg:text-[32px] font-extrabold text-[#1A2B4A] mb-6">How to Reach Us</h2>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-phone-line', title: 'Phone', content: <a href="tel:+12695551234" className="text-[18px] text-[#1A2B4A] hover:text-[#C8A96E] transition-colors cursor-pointer font-bold">(269) 555-1234</a>, sub: 'Call or text us anytime' },
                  { icon: 'ri-message-3-line', title: 'Text Message', content: <a href="sms:+12695551234" className="text-[15px] text-[#C8A96E] hover:text-[#b8924e] transition-colors cursor-pointer font-semibold">Send us a text</a>, sub: 'Quick and easy way to reach us' },
                  { icon: 'ri-map-pin-line', title: 'Service Area', content: <p className="text-[15px] text-[#6B7280] leading-relaxed">Kalamazoo, Portage, Plainwell,<br />Three Rivers, Otsego &amp; surrounding Southwest Michigan</p>, sub: null },
                  { icon: 'ri-time-line', title: 'Response Time', content: <p className="text-[15px] text-[#6B7280]">We usually respond within 24 hours</p>, sub: null },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white rounded-xl p-4 shadow-sm">
                    <div className="w-10 h-10 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-lg text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-[14px] font-bold text-[#1A2B4A] mb-1">{item.title}</h3>
                      {item.content}
                      {item.sub && <p className="text-[13px] text-[#6B7280] mt-0.5">{item.sub}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-[#10B981] rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-translate-2 text-2xl text-white"></i>
                  <h3 className="text-[20px] font-extrabold text-white">Hablamos Español</h3>
                </div>
                <p className="text-[14px] text-white/90 leading-relaxed">
                  We speak English and Spanish. Talk to us in whichever language is easier for you.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <form
                id="quote-request"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-xl p-7 shadow-md border border-gray-100"
              >
                <p className="text-[12px] text-[#C8A96E] uppercase tracking-widest font-bold mb-1">Free Estimate</p>
                <h3 className="text-[24px] font-extrabold text-[#1A2B4A] mb-1">Request a Free Estimate</h3>
                <p className="text-[14px] text-[#6B7280] mb-6">Fill out the form and we'll get back to you soon</p>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">Your Name *</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all text-[14px]"
                        placeholder="John Smith" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">Phone Number *</label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all text-[14px]"
                        placeholder="(269) 555-1234" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">Email Address *</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all text-[14px]"
                        placeholder="john@example.com" />
                    </div>
                    <div>
                      <label htmlFor="city" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">City *</label>
                      <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all text-[14px]"
                        placeholder="Kalamazoo" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="serviceNeeded" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">Service Needed *</label>
                    <select id="serviceNeeded" name="serviceNeeded" value={formData.serviceNeeded} onChange={handleChange} required
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all text-[14px]">
                      <option value="">Select a service</option>
                      <option value="Interior Painting">Interior Painting</option>
                      <option value="Exterior Painting">Exterior Painting</option>
                      <option value="Cabinet Painting">Cabinet Painting</option>
                      <option value="Fence Staining">Fence Staining</option>
                      <option value="Deck Staining">Deck Staining</option>
                      <option value="Multiple Services">Multiple Services</option>
                      <option value="Not Sure">Not Sure Yet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="projectDetails" className="block text-[13px] font-bold text-[#1A2B4A] mb-1.5">Project Details</label>
                    <textarea id="projectDetails" name="projectDetails" value={formData.projectDetails} onChange={handleChange}
                      rows={3} maxLength={500}
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:border-[#1A2B4A] focus:ring-2 focus:ring-[#1A2B4A]/10 outline-none transition-all resize-none text-[14px]"
                      placeholder="Tell us about your project (optional, max 500 characters)"></textarea>
                    <p className="text-[12px] text-[#6B7280] mt-1">{formData.projectDetails.length}/500 characters</p>
                  </div>

                  <button type="submit" disabled={isSubmitting}
                    className="w-full bg-[#1A2B4A] text-white px-8 py-3.5 rounded-lg font-bold text-[15px] hover:bg-[#C8A96E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer">
                    {isSubmitting ? 'Sending...' : 'Request Free Estimate'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-[#10B981]/10 border border-[#10B981] text-[#10B981] px-4 py-3 rounded-lg text-[14px]">
                      <strong>Thanks!</strong> We received your request and will get back to you soon.
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-[14px]">
                      Something went wrong. Please call us at (269) 555-1234 instead.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
